import { PageType } from 'api/page-template/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetDefaultAiSectionCommands,
  useGetDefaultEmailBlockCommands,
  useGetDefaultMeetingBlockCommands,
} from 'components/organisms/document-editor/extensions/command-picker/commands-list/default-ai-section-commands';
import { useGetTemplateSectionsCommands } from 'components/organisms/document-editor/extensions/command-picker/commands-list/meeting-template-sections';
import { CommandPickerItem } from 'components/organisms/document-editor/extensions/command-picker/types';

export const useCommandsForCommandPicker = ({
  pageType,
}: {
  pageType?: PageType;
}) => {
  const aiSectionCommands = useGetDefaultAiSectionCommands(pageType);
  const meetingBlockCommands = useGetDefaultMeetingBlockCommands();
  const emailBlockCommands = useGetDefaultEmailBlockCommands();
  const meetingTemplateSectionsCommands = useGetTemplateSectionsCommands({
    pageType: pageType,
  });

  const blockCommands = useMemo(() => {
    switch (pageType) {
      case PageType.Meeting:
        return meetingBlockCommands;
      case PageType.Email:
        return emailBlockCommands;
      default:
        return [];
    }
  }, [pageType, meetingBlockCommands, emailBlockCommands]);

  // Filter out signal block for email page type
  const filteredAiSectionCommands = useMemo(() => {
    if (pageType === PageType.Email) {
      return aiSectionCommands.map((commandGroup) => {
        // Check if it's a group item with items property
        if ('items' in commandGroup) {
          return {
            ...commandGroup,
            items: commandGroup.items.filter((item) => item.id !== 'signal'),
          };
        }

        return commandGroup;
      });
    }

    return aiSectionCommands;
  }, [pageType, aiSectionCommands]);

  return useMemo(
    () => ({
      items: [
        ...filteredAiSectionCommands,
        ...blockCommands,
        ...meetingTemplateSectionsCommands.items,
      ],
      isLoading: meetingTemplateSectionsCommands.isLoading,
    }),
    [filteredAiSectionCommands, blockCommands, meetingTemplateSectionsCommands],
  );
};
export const useCommandsForCommandPickerAsPromise = ({
  pageType,
}: {
  pageType?: PageType;
}) => {
  const [resolveFunction, setResolveFunction] = useState<
    ((items: CommandPickerItem[]) => void) | null
  >(null);

  const commandsForCommandPicker = useCommandsForCommandPicker({
    pageType,
  });

  useEffect(() => {
    if (!commandsForCommandPicker.isLoading && resolveFunction) {
      resolveFunction(commandsForCommandPicker.items || []);
      setResolveFunction(null);
    }
  }, [commandsForCommandPicker, resolveFunction]);

  return useCallback(() => {
    return new Promise<CommandPickerItem[]>((resolve) => {
      if (!commandsForCommandPicker.isLoading) {
        resolve(commandsForCommandPicker.items || []);
      } else {
        setResolveFunction(() => resolve);
      }
    });
  }, [commandsForCommandPicker, setResolveFunction]);
};
